import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserProfile } from '../types/auth.types';
import { CookieService } from './cookie.service';
import { AUTH_CONSTANTS } from '../constants/auth.constants';

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  private readonly cookieService = inject(CookieService);
  private readonly userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  private readonly profilePictureSubject = new BehaviorSubject<string | null>(null);
  public readonly userProfile$ = this.userProfileSubject.asObservable();
  public readonly profilePicture$ = this.profilePictureSubject.asObservable();

  loadUserProfile(userClaims: any): void {
    const userProfile: UserProfile = {
      firstName: userClaims?.given_name,
      lastName: userClaims?.family_name,
      email: userClaims?.email,
      fullName: userClaims?.name
    };
    this.userProfileSubject.next(userProfile);
    this.saveUserToCookie(userClaims);
    // Fetch profile picture if email is available
    if (userProfile.email) {
      this.fetchProfilePicture(userProfile.email);
    }
  }

  fetchProfilePicture(email: string): void {
    // Check if we have cached profile picture
    const cachedPicture = this.getCachedProfilePicture(email);
    if (cachedPicture) {
      this.profilePictureSubject.next(cachedPicture);
      return;
    }
    this.tryGravatar(email);
  }

  private tryGravatar(email: string): void {
    const hashedEmail = this.hashEmail(email);
    const gravatarUrl = `https://www.gravatar.com/avatar/${hashedEmail}?d=404&s=96`;
    const img = new Image();
    img.onload = () => {
      this.profilePictureSubject.next(gravatarUrl);
      this.cacheProfilePicture(email, gravatarUrl);
    };
    img.onerror = () => {
      // No Gravatar found, use null to show initials
      this.profilePictureSubject.next(null);
    };
    img.src = gravatarUrl;
  }

  private hashEmail(email: string): string {
    const trimmedEmail = email.trim().toLowerCase();
    let hash = 0;
    if (trimmedEmail.length === 0) return hash.toString();
    for (let i = 0; i < trimmedEmail.length; i++) {
      const char = trimmedEmail.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }


  private getCachedProfilePicture(email: string): string | null {
    const cacheKey = `profile_picture_${email}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const now = Date.now();
        // Check if cache is still valid (24 hours)
        if (now - parsed.timestamp < parsed.expiry) {
          return parsed.url;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey);
        }
      } catch (error) {
        // Invalid cache format, remove it
        localStorage.removeItem(cacheKey);
      }
    }
    return null;
  }

  private cacheProfilePicture(email: string, photoUrl: string): void {
    const cacheKey = `profile_picture_${email}`;
    // Cache for 24 hours
    const cacheData = {
      url: photoUrl,
      timestamp: Date.now(),
      expiry: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  getUserFromCookie(): any {
    const cookieValue = this.cookieService.getCookie(AUTH_CONSTANTS.COOKIE_NAMES.USER_DETAILS);
    return cookieValue ? JSON.parse(cookieValue) : null;
  }

  clearUserProfile(): void {
    this.userProfileSubject.next(null);
    this.profilePictureSubject.next(null);
    this.cookieService.deleteCookie(AUTH_CONSTANTS.COOKIE_NAMES.USER_DETAILS);
  }

  private saveUserToCookie(userClaims: any): void {
    if (!this.cookieService.hasCookie(AUTH_CONSTANTS.COOKIE_NAMES.USER_DETAILS)) {
      this.cookieService.setCookie(
        AUTH_CONSTANTS.COOKIE_NAMES.USER_DETAILS,
        JSON.stringify(userClaims),
        AUTH_CONSTANTS.COOKIE_EXPIRY_DAYS.USER_SESSION
      );
    }
  }
}
